import clsx from 'clsx';
import {
  ComponentPropsWithoutRef,
  forwardRef,
  ReactElement,
  ReactNode,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { isAntiFilterOp, StackedFilter } from 'src/components/Filters';
import {
  Content,
  isFormatContentId,
  useContent,
  useFormattedContent,
} from 'src/contexts/ContentContext';
import { vars } from 'src/core/themes';
import { Button, ButtonProps, Stack } from 'src/core/ui';
import { useFlexSearchIndex } from 'src/hooks/useFlexSearchIndex';
import {
  CheckIcon,
  CrossIcon,
  ExpandIcon,
  FoldIcon,
  IconsFill,
} from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import { FormatContentId } from 'src/utils/constants/formatContentId';
import { FilterOperationType } from 'src/WebApiController';

import { PosDropdown } from '../PosDropdown';
import { PosSelect } from '../PosSelect';
import { PosSpinner } from '../PosSpinner';
import { PosTextField } from '../PosTextField';
import * as styles from './PosMultiSelect.css';
import {
  GroupedOptionsConfig,
  MultiSelectValueChange,
} from './PosMultiSelect.types';
import {
  isToggleAllValue,
  PosMultiSelectOptions,
} from './PosMultiSelectOptions';

const MIN_ITEMS_COUNT_TO_HIDE_SELECT_ALL = 50;

type ContentFooterProps = {
  disabled?: boolean;
  primaryButtonContentId: ContentId;
  primaryButtonVariant?: ButtonProps['variant'] | null;
  onPrimaryClick: () => void;
  secondaryButtonContentId?: ContentId;
  onSecondaryClick?: () => void;
};

export type PosMultiSelectProps = Omit<
  ComponentPropsWithoutRef<typeof PosDropdown>,
  'onChange'
> & {
  values: string[];
  showNewOption?: boolean;
  placeholderText?: string | ContentId;
  /**
   * Placeholder text to use when `operation` is an anti-filter operation.
   * Defaults to `ContentId.NoneSelected`.
   */
  placeholderTextAntiFilter?: string | ContentId;
  enableEmptySelection?: boolean;
  hasErrors?: boolean;
  onChange: (change: MultiSelectValueChange<string>) => void;
  leftIcon?: ReactNode;
  showClearButton?: boolean;
  closeAfterToggleAll?: boolean;
  valueOptionsContent: Record<string, ContentId | string>;
  valueOptionsIcon?: Record<string, ReactNode>;
  valueOptionsPostfixIcon?: Record<string, ReactNode>;
  /**
   * (Optional) A map of operation options to their content ids.
   * If provided, the options will be displayed as buttons in the dropdown.
   * And then the selected operation will be passed back in the onChange callback.
   */
  operationOptionsContent?: Partial<
    Record<FilterOperationType, ContentId | string>
  >;
  /**
   * The currently selected operation.
   * This is passed back in the onChange callback.
   */
  operation?: FilterOperationType | null;
  /**
   * Whether to layout the options horizontally or vertically - this affects the layout
   * when operationOptionsContent is provided.
   * Defaults to vertical.
   */
  isHorizontalLayout?: boolean;
  disabled?: boolean;
  loading?: boolean;
  searchable?: boolean;
  /* this needs searchable to be true */
  allowSearchAsInput?: boolean;
  sortMode?: 'ascending' | 'descending' | 'none';
  sortFn?: (a: string, b: string) => number;
  useVirtualWindow?: boolean;
  expectedItemSizeForVirtualWindow?: number;
  contentHeight?: string;
  fullWidth?: boolean;
  showSelectedOptionsFirst?: boolean;
  showSelectAllOption?: boolean;
  fetchNextPage?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  onSearchTextChange?: (searchText: string) => void;
  includeSelectedInSearch?: boolean;
  valuesDisplayText?: string | null;
  prefixDisplay?: React.ReactNode;
  childrenAsTrigger?: boolean;
  pluralDisplayFormatContentId?: FormatContentId;
  valueOptionsDisplay?: Record<string, () => JSX.Element>;
  valueOptionsSort?: Record<string, string>;
  firstRenderOpen?: boolean;
  contentFooterProps?: ContentFooterProps;
  /**
   * (Optional) Configuration for grouping options with section headers.
   * When provided, options will be displayed under their group headers.
   */
  groupedOptionsConfig?: GroupedOptionsConfig;
};

function getValueDisplay(
  values: string[],
  valueOptionsContent: Record<string, string>,
  showNewOption: boolean,
  pluralDisplayFormatContentId?: string
): string | null {
  // we only display value when we have only 1 value selected
  if (values.length === 1) {
    const value = values[0];
    const hasValue = values[0] in valueOptionsContent;
    return showNewOption
      ? hasValue
        ? valueOptionsContent[value]
        : value
      : valueOptionsContent[value];
  }

  if (values.length > 1) {
    return pluralDisplayFormatContentId ?? FormatContentId.ItemsSelected;
  }

  return null;
}

export const PosMultiSelect = forwardRef<HTMLDivElement, PosMultiSelectProps>(
  function PosMultiSelect(
    {
      values,
      showNewOption,
      placeholderText,
      placeholderTextAntiFilter,
      enableEmptySelection,
      hasErrors,
      onChange,
      leftIcon,
      valueOptionsContent,
      valueOptionsIcon,
      valueOptionsPostfixIcon,
      operation,
      operationOptionsContent,
      isHorizontalLayout,
      disabled,
      loading,
      showClearButton,
      closeAfterToggleAll,
      searchable,
      allowSearchAsInput,
      rootProps,
      sortMode,
      sortFn,
      useVirtualWindow,
      expectedItemSizeForVirtualWindow,
      contentHeight,
      fullWidth,
      showSelectedOptionsFirst,
      showSelectAllOption: showSelectAllOptionProp,
      fetchNextPage,
      hasNextPage,
      isFetchingNextPage,
      onSearchTextChange,
      includeSelectedInSearch,
      valuesDisplayText,
      prefixDisplay,
      childrenAsTrigger,
      pluralDisplayFormatContentId,
      valueOptionsDisplay,
      valueOptionsSort,
      firstRenderOpen,
      contentFooterProps,
      groupedOptionsConfig,
      ...rest
    },
    ref
  ): ReactElement {
    const [searchText, setSearchText] = useState('');
    const [isOpen, setIsOpen] = useState(firstRenderOpen ?? false);

    // Sort keys by the sort value

    const showSelectAllOption = useMemo<boolean>(() => {
      if (typeof showSelectAllOptionProp === 'boolean') {
        return showSelectAllOptionProp;
      }
      return (
        Object.keys(valueOptionsContent).length <
        MIN_ITEMS_COUNT_TO_HIDE_SELECT_ALL
      );
    }, [showSelectAllOptionProp, valueOptionsContent]);

    const { flexSearchIndex } = useFlexSearchIndex(
      valueOptionsContent,
      searchable,
      isOpen
    );

    const isBuildingSearchIndex = searchable && !flexSearchIndex;

    const onOpenChange = useCallback(
      (open: boolean) => {
        setIsOpen(open);
        rootProps?.onOpenChange?.(open);
      },
      [rootProps]
    );

    const valueDisplay = getValueDisplay(
      values,
      valueOptionsContent,
      showNewOption ?? false,
      pluralDisplayFormatContentId
    );

    const singleValueContentDisplay = useContent(valueDisplay ?? undefined);
    const valueContentDisplay =
      useFormattedContent(
        valueDisplay && isFormatContentId(valueDisplay)
          ? valueDisplay
          : FormatContentId.EmptyContent,
        `${values.length}`
      ) || singleValueContentDisplay;

    const placeholderTextContent = useContent(placeholderText);
    const placeholderTextContentAntiFilter = useContent(
      placeholderTextAntiFilter ?? ContentId.NoneSelected
    );
    const noneContent = useContent(ContentId.None);

    const isAntiFilter = isAntiFilterOp(operation);

    const mergeSelectedValue = useCallback(
      (newValue: string | null) => {
        const isToggleAll = isToggleAllValue(newValue);
        const allValuesKeys = Object.keys(valueOptionsContent);
        const areValuesSelected = values.length === allValuesKeys.length;

        if (isToggleAll) {
          if (showSelectAllOption) {
            return areValuesSelected ? [] : allValuesKeys;
          } else {
            // It's clear all option, then clear all
            return [];
          }
        }

        if (newValue && values.includes(newValue)) {
          // Unselect
          return values.filter((v) => v !== newValue);
        }

        // If we allow empty selections, just clear the selected values
        if (enableEmptySelection && !newValue) {
          return [];
        }

        return newValue ? [...values, newValue] : [...values];
      },
      [valueOptionsContent, values, showSelectAllOption, enableEmptySelection]
    );

    const searchPlaceholder = useContent(
      allowSearchAsInput ? ContentId.SearchOrEnter : ContentId.Search
    );

    const onSearchTextApplied = useCallback(
      (searchText: string) => {
        const existingValue = Object.keys(valueOptionsContent).find(
          (k) =>
            valueOptionsContent[k]?.toLocaleUpperCase() ===
            searchText.toLocaleUpperCase()
        );

        if (existingValue) {
          onChange({ newValues: mergeSelectedValue(existingValue) });
          if (isToggleAllValue(existingValue) && closeAfterToggleAll) {
            setIsOpen(false);
          }
        } else if (allowSearchAsInput) {
          onChange({ newValues: values, searchText });
          setIsOpen(false);
        }
      },
      [
        allowSearchAsInput,
        closeAfterToggleAll,
        mergeSelectedValue,
        onChange,
        valueOptionsContent,
        values,
      ]
    );

    const searchBox = useMemo(
      () => (
        <div className={styles.seachBoxContainer}>
          <PosTextField
            ref={(input: HTMLInputElement) => input?.focus()}
            value={searchText}
            placeholder={searchPlaceholder}
            onChange={(e) => {
              setSearchText(e.target.value);
              onSearchTextChange?.(e.target.value);
            }}
            onKeyDown={(e) => {
              if (!disabled) {
                const key = e.key;
                if (key === 'Enter') {
                  e.stopPropagation();

                  // See if search-text matches any option, return it as a selection, else return searchText
                  if (searchText) {
                    onSearchTextApplied(searchText);
                  }
                }
              }
            }}
            postfixDisplay={
              searchText && (
                <>
                  {allowSearchAsInput && (
                    <CheckIcon
                      fill={IconsFill.textSuccess}
                      withHoverEffect
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        onSearchTextApplied(searchText);
                      }}
                    />
                  )}
                  <CrossIcon
                    size={vars.iconSize.m}
                    withHoverEffect
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      setSearchText('');
                      onSearchTextChange?.('');
                    }}
                  />
                </>
              )
            }
          />
        </div>
      ),
      [
        allowSearchAsInput,
        disabled,
        onSearchTextApplied,
        onSearchTextChange,
        searchPlaceholder,
        searchText,
      ]
    );

    return (
      <StackedFilter
        isHorizontalLayout={isHorizontalLayout}
        renderSingleDirectly
        FilterComponents={[
          ...(operationOptionsContent
            ? [
                <PosSelect
                  key="operationFilter"
                  value={operation ?? undefined}
                  valueOptionsContent={operationOptionsContent}
                  enableEmptySelection={false}
                  onChange={(newOperation) => {
                    onChange({
                      newValues: values,
                      operation: newOperation as FilterOperationType,
                    });
                  }}
                  capitalizeOptions={true}
                />,
              ]
            : []),
          <PosDropdown
            key={'mainFilter'}
            ref={ref}
            {...rest}
            rootProps={{
              ...rootProps,
              open: isOpen,
              onOpenChange,
            }}
            triggerProps={{
              disabled,
              ...rest.triggerProps,
            }}
            trigger={
              <>
                {childrenAsTrigger ? (
                  <>{rest.children}</>
                ) : (
                  <Button
                    variant="outline"
                    shape="rect"
                    textColor="strong"
                    className={clsx(styles.posMultiSelectTrigger, {
                      [styles.error]: hasErrors,
                      [styles.triggerFullWidth]: fullWidth,
                    })}
                    disabled={disabled}
                    {...rest.triggerProps}
                  >
                    <Stack
                      className={styles.posMultiSelectContent}
                      gap="m"
                      alignItems="center"
                      width="full"
                    >
                      {prefixDisplay}
                      {leftIcon}
                      <div
                        className={clsx('value-selected', styles.valueWrapper)}
                      >
                        {valuesDisplayText
                          ? valuesDisplayText
                          : !valueDisplay
                            ? isAntiFilter
                              ? placeholderTextContentAntiFilter ||
                                placeholderTextAntiFilter
                              : placeholderTextContent || placeholderText
                            : valueContentDisplay}
                      </div>
                    </Stack>
                    <Stack gap="m" width="fit" alignItems="center">
                      {showClearButton && values.length > 0 && (
                        <CrossIcon
                          disabled={disabled}
                          withHoverEffect
                          size={vars.iconSize.m}
                          onClick={(e) => {
                            e.stopPropagation();
                            onChange({
                              newValues: [],
                              searchText: null,
                              operation,
                            });
                          }}
                        />
                      )}
                      {isOpen ? (
                        <FoldIcon
                          withHoverEffect
                          disabled={disabled}
                          size={vars.iconSize.s}
                          minSize={vars.iconSize.s}
                          align={'middle'}
                        />
                      ) : (
                        <ExpandIcon
                          withHoverEffect
                          disabled={disabled}
                          size={vars.iconSize.s}
                          minSize={vars.iconSize.s}
                          align={'middle'}
                        />
                      )}
                    </Stack>
                  </Button>
                )}
              </>
            }
          >
            {loading || isBuildingSearchIndex ? (
              <PosSpinner size={vars.iconSize.s} />
            ) : (
              <>
                {searchable && searchBox}
                <PosMultiSelectOptions
                  selectedOptions={values}
                  onChange={(v) => {
                    onChange({
                      newValues: mergeSelectedValue(v),
                      searchText: null,
                      operation,
                    });
                    if (isToggleAllValue(v) && closeAfterToggleAll) {
                      setIsOpen(false);
                    }
                  }}
                  valueOptionsContent={valueOptionsContent}
                  valueOptionsDisplay={valueOptionsDisplay}
                  valueOptionsSort={valueOptionsSort}
                  valueOptionsIcon={valueOptionsIcon}
                  valueOptionsPostfixIcon={valueOptionsPostfixIcon}
                  searchText={searchText}
                  emptySelectionText={
                    enableEmptySelection
                      ? isAntiFilter
                        ? placeholderTextAntiFilter || noneContent
                        : placeholderText
                      : undefined
                  }
                  sortMode={sortMode}
                  sortFn={sortFn}
                  useVirtualWindow={useVirtualWindow}
                  expectedItemSizeForVirtualWindow={
                    expectedItemSizeForVirtualWindow
                  }
                  contentHeight={
                    contentHeight
                      ? contentHeight
                      : searchable
                        ? 'calc(var(--radix-popper-available-height) - 60px - 16px)'
                        : 'calc(var(--radix-popper-available-height) - 16px)'
                  }
                  flexSearchIndex={flexSearchIndex}
                  showSelectedOptionsFirst={showSelectedOptionsFirst}
                  showSelectAllOption={showSelectAllOption}
                  fetchNextPage={fetchNextPage}
                  hasNextPage={hasNextPage}
                  isFetchingNextPage={isFetchingNextPage}
                  includeSelectedInSearch={includeSelectedInSearch}
                  groupedOptionsConfig={groupedOptionsConfig}
                />
                {contentFooterProps && (
                  <>
                    <div className={styles.footerDividerLine} />
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="spaceBetween"
                      className={styles.footerContainer}
                    >
                      <div>
                        {contentFooterProps.secondaryButtonContentId && (
                          <Button
                            variant="textPlain"
                            disabled={contentFooterProps.disabled}
                            onClick={() => {
                              setIsOpen(false);
                              contentFooterProps.onSecondaryClick?.();
                            }}
                          >
                            <Content
                              id={contentFooterProps.secondaryButtonContentId}
                            />
                          </Button>
                        )}
                      </div>
                      <Button
                        variant={
                          contentFooterProps.primaryButtonVariant ?? 'regular'
                        }
                        disabled={contentFooterProps.disabled}
                        onClick={() => {
                          setIsOpen(false);
                          contentFooterProps.onPrimaryClick();
                        }}
                      >
                        {contentFooterProps.primaryButtonContentId ? (
                          <Content
                            id={contentFooterProps.primaryButtonContentId}
                          />
                        ) : (
                          <Content id={ContentId.Save} />
                        )}
                      </Button>
                    </Stack>
                  </>
                )}
              </>
            )}
          </PosDropdown>,
        ]}
      />
    );
  }
);
